import { STATUS } from '@front-app/constants/state';
import cloneDeep from 'clone-deep';
import {
	computed,
	Ref,
	watch,
} from '@vue/composition-api';
import {
	GroupMember,
	GroupMemberCounts,
	GroupMemberWithEmail,
	UserRole,
} from '@front-app/services/UserApi/UserModel';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import {
	getGroupMemberCounts,
	getGroupMembersV2,
	removeGroupMembers,
} from '@front-app/services/GroupsApi/GroupsMemberApi';
import { sum } from '@front-app/util/math';
import { GroupMemberListRole } from '@front-app/modules/groupMember/hooks/useFilterMemberList';
import { STARTABLE_STATUSES, StoreCache, useCache } from '../util/cache';
import { UserStore } from './user';
import { GroupsStore } from './groups';

type StoreMember = GroupMember | GroupMemberWithEmail;
type MemberApi = (groupId: string) => Promise<StoreMember[]>;

export interface GroupMembersStore {
	status: Ref<STATUS>;
	fetch(id: string, force?: boolean): Promise<void>;
	removeMembers(groupId: string, userIds: string[], externalUserIds: string[]): Promise<void>;
	getAdministrators(): StoreMember[];
	getNumMembersByRole(requestedRole: UserRole[]): number;
}

export const getGroupMembersStore = (
	currentGroupId: Ref<string>,
	outerMemberCache: StoreCache<string, StoreMember[]> = {},
	outerCountsCache: StoreCache<string, GroupMemberCounts|null> = {},
	userStore: UserStore,
	groupsStore: GroupsStore,
): GroupMembersStore => {
	const memberCache = useCache(outerMemberCache, () => []);
	const countsCache = useCache(outerCountsCache, () => null);

	const fetchGroupMembers = async (api: MemberApi, groupId: string, force: boolean) => {
		if (force || STARTABLE_STATUSES.includes(memberCache.getStatus(groupId).value)) {
			const { track } = useAsyncStatus(memberCache.getStatus(groupId));
			const fetchData = async () => {
				const result = await api(groupId);
				if (result) {
					memberCache.set(groupId, result);
					// Fetch full data for non-external invitees
					const nonExternalUsers = result.filter(({ groupRole }) => groupRole !== UserRole.EXTERNAL_INVITEE);
					// Clone Deep is needed to prevent side effects from the user store. Need to determine why those are happening
					userStore.setPartials(cloneDeep(nonExternalUsers));
				}
			};
			await track(fetchData());
		}
	};

	const fetch = async (id: string, force = false): Promise<void> => {
		await groupsStore.fetchGroupsOnce();
		await fetchGroupMembers(getGroupMembersV2, id, force);
	};

	const getAdministrators = () => memberCache.get(currentGroupId.value);

	const status: Ref<STATUS> = computed(() => memberCache.getStatus(currentGroupId.value).value);

	const removeMembers = async (groupId: string, userIds: string[], externalUserIds: string[]) => {
		await removeGroupMembers(groupId, userIds, externalUserIds);
		const updated = memberCache.get(groupId).filter(({ id }) => !userIds.includes(id) && !externalUserIds.includes(id));
		memberCache.set(groupId, updated);
	};

	const fetchCounts = async (groupId: string) => {
		if (STARTABLE_STATUSES.includes(countsCache.getStatus(groupId).value)) {
			const { track } = useAsyncStatus(countsCache.getStatus(groupId));
			countsCache.set(groupId, await track(getGroupMemberCounts(groupId)));
		}
	};

	const getNumMembersByRole = (requestedRoles: GroupMemberListRole[]) => {
		const groupId = currentGroupId.value;
		const val = countsCache.get(groupId);
		if (!val) {
			return 0;
		}
		return requestedRoles.map((role) => val[role])
			.filter(Boolean)
			.reduce(sum, 0);
	};

	watch(currentGroupId, (curr, prev) => {
		if (curr === prev) {
			return;
		}
		fetch(currentGroupId.value);
		fetchCounts(currentGroupId.value);
	}, { immediate: true });

	return {
		status,
		fetch,
		removeMembers,
		getAdministrators,
		getNumMembersByRole,
	};
};
